// Renders CodecViews (spectra, predictions, bars, text) inside the inspector.
import type { BarsView, CodecView, PredictionView, SpectrumView } from '../codecs/types';
import { h, hideTip, richText, showTip } from './dom';
import { alpha, theme } from './theme';

const REGION_COLOR: Record<string, string> = {
  coded: '#2dd4bf', small: '#38bdf8', zero: '#475569', noise: '#f472b6', intensity: '#a78bfa',
};

export function renderViews(views: CodecView[]): HTMLElement {
  const wrap = h('div', { class: 'cviews' });
  const texts = views.filter((v) => v.kind === 'text');
  const charts = views.filter((v) => v.kind === 'spectrum' || v.kind === 'prediction');
  const bars = views.filter((v) => v.kind === 'bars') as BarsView[];
  for (const t of texts) if (t.kind === 'text') wrap.append(h('div', { class: 'cv-text' }, h('h4', {}, t.title), h('p', { html: richText(t.body) })));
  if (charts.length) {
    const grid = h('div', { class: `cv-grid${charts.length > 1 ? ' two' : ''}` });
    for (const v of charts) grid.append(v.kind === 'spectrum' ? spectrumChart(v) : predictionChart(v as PredictionView));
    wrap.append(grid);
  }
  if (bars.length) {
    const grid = h('div', { class: `cv-grid${bars.length > 1 ? ' two' : ''}` });
    for (const b of bars) grid.append(barsChart(b));
    wrap.append(grid);
  }
  return wrap;
}

/** Draws once the element is in the page and sized, and again on resize. */
function whenSized(cv: HTMLCanvasElement, height: number, draw: (c: CanvasRenderingContext2D, w: number, h: number) => void) {
  const paint = () => {
    const w = cv.clientWidth;
    if (!w) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(height * dpr);
    const c = cv.getContext('2d')!;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, w, height);
    draw(c, w, height);
  };
  cv.style.height = height + 'px';
  new ResizeObserver(paint).observe(cv);
}

function fmtHz(f: number) {
  return f >= 1000 ? `${(f / 1000).toFixed(f >= 10000 ? 1 : 2)} kHz` : `${Math.round(f)} Hz`;
}

// ------------------------------------------------------------------ spectrum
function spectrumChart(v: SpectrumView): HTMLElement {
  const n = v.coeffs.length;
  const H = 176, PLOT = 130, STRIP = 14;
  const cv = h('canvas', { class: 'cv-canvas' });
  let peak = 0, nonzero = 0, lastNz = -1;
  for (let i = 0; i < n; i++) {
    const a = Math.abs(v.coeffs[i]);
    if (a > peak) peak = a;
    if (a > 0) { nonzero++; lastNz = i; }
  }
  const binHz = v.nyquist / n;
  const DB = 72;
  const yOf = (a: number) => (a <= 0 ? PLOT : Math.min(PLOT, Math.max(0, (-20 * Math.log10(a / peak) / DB) * PLOT)));
  whenSized(cv, H, (c, W) => {
    const T = theme();
    // Band shading.
    v.bands?.forEach((b, i) => {
      if (i % 2) return;
      c.fillStyle = alpha(T.text, 0.045);
      c.fillRect((b.start / n) * W, 0, ((b.end - b.start) / n) * W, PLOT);
    });
    // Zero regions shaded slate so "nothing stored" is visible.
    v.regions?.filter((r) => r.kind === 'zero').forEach((r) => {
      c.fillStyle = alpha('#475569', 0.18);
      c.fillRect((r.start / n) * W, 0, ((r.end - r.start) / n) * W, PLOT);
    });
    // Coefficient magnitudes (stems, or per-pixel maxima when bins are narrower than a pixel).
    c.fillStyle = T.kind.audio;
    if (peak > 0) {
      if (n <= W) {
        const bw = W / n;
        for (let i = 0; i < n; i++) {
          const a = Math.abs(v.coeffs[i]);
          if (a <= 0) continue;
          const y = yOf(a);
          c.fillRect(i * bw + (bw > 3 ? 0.5 : 0), y, Math.max(1, bw - (bw > 3 ? 1 : 0)), PLOT - y);
        }
      } else {
        for (let x = 0; x < W; x++) {
          const a0 = Math.floor((x / W) * n), a1 = Math.floor(((x + 1) / W) * n);
          let mx = 0;
          for (let i = a0; i < a1; i++) mx = Math.max(mx, Math.abs(v.coeffs[i]));
          if (mx > 0) { const y = yOf(mx); c.fillRect(x, y, 1, PLOT - y); }
        }
      }
    }
    c.fillStyle = T.line;
    c.fillRect(0, PLOT, W, 1);
    // Regions strip.
    c.font = '10px Inter, sans-serif';
    v.regions?.forEach((r) => {
      const x0 = (r.start / n) * W, x1 = (r.end / n) * W;
      c.fillStyle = alpha(REGION_COLOR[r.kind] ?? T.muted, r.kind === 'zero' ? 0.5 : 0.8);
      c.fillRect(x0, PLOT + 2, Math.max(1, x1 - x0 - 1), STRIP - 2);
      if (x1 - x0 > c.measureText(r.label).width + 8) {
        c.fillStyle = '#0b0e14';
        c.fillText(r.label, x0 + 4, PLOT + STRIP - 3);
      }
    });
    // Frequency axis.
    c.fillStyle = T.muted;
    c.font = '10px "JetBrains Mono", monospace';
    const stepK = v.nyquist > 16000 ? 5 : v.nyquist > 8000 ? 2 : 1;
    for (let k = 0; k * 1000 < v.nyquist; k += stepK) {
      const x = ((k * 1000) / v.nyquist) * W;
      c.fillRect(x, PLOT + STRIP + 1, 1, 4);
      c.fillText(`${k}k`, Math.min(W - 16, x + 2), H - 4);
    }
  });
  cv.addEventListener('pointermove', (e) => {
    const r = cv.getBoundingClientRect();
    const i = Math.max(0, Math.min(n - 1, Math.floor(((e.clientX - r.left) / r.width) * n)));
    const band = v.bands?.find((b) => i >= b.start && i < b.end);
    const reg = v.regions?.find((b) => i >= b.start && i < b.end);
    const val = v.coeffs[i];
    showTip(`<b>Line ${i}</b> · ${fmtHz(i * binHz)}–${fmtHz((i + 1) * binHz)}<br><span class="mono">value ${val === 0 ? '0 (nothing stored)' : val.toPrecision(4)}</span>${band ? `<div class="tt-k">band: ${richText(band.label)}</div>` : ''}${reg ? `<div class="tt-k">region: ${richText(reg.label)}</div>` : ''}`, e.clientX, e.clientY);
  });
  cv.addEventListener('pointerleave', hideTip);
  const meta = `${n} lines · ${Math.round((nonzero / n) * 100)}% non-zero${lastNz >= 0 ? ` · top ${fmtHz((lastNz + 1) * binHz)}` : ''}${v.bits !== undefined ? ` · ${v.bits.toLocaleString('en-US')} bits` : ''}`;
  return h('figure', { class: 'cv-fig' },
    h('figcaption', {}, h('b', {}, v.title), h('span', { class: 'cv-meta' }, meta)),
    cv,
    h('p', { class: 'cv-cap', html: richText(v.caption) }));
}

// ------------------------------------------------------------------ prediction
function predictionChart(v: PredictionView): HTMLElement {
  const N = v.signal.length;
  const H = 190, TOP = 110, GAP = 16, BOT = H - TOP - GAP - 14;
  const cv = h('canvas', { class: 'cv-canvas' });
  let span: [number, number] = [0, N];
  let peak = 1;
  for (let i = 0; i < N; i++) peak = Math.max(peak, Math.abs(v.signal[i]), Math.abs(v.residual[i]));
  let resPeak = 1;
  for (let i = 0; i < N; i++) resPeak = Math.max(resPeak, Math.abs(v.residual[i]));
  let redraw = () => {};
  whenSized(cv, H, (c, W) => {
    redraw = () => {
      const T = theme();
      c.clearRect(0, 0, W, H);
      const [a, b] = span;
      const len = b - a;
      const xOf = (i: number) => ((i - a + 0.5) / len) * W;
      const lane = (y0: number, hh: number, data: Int32Array, col: string, dashed = false) => {
        const mid = y0 + hh / 2;
        const sc = hh / 2 / peak; // same scale for signal and residual: that is the point
        c.strokeStyle = col;
        c.fillStyle = col;
        c.lineWidth = 1.2;
        c.setLineDash(dashed ? [4, 3] : []);
        if (len <= W) {
          c.beginPath();
          for (let i = a; i < b; i++) { const x = xOf(i), y = mid - data[i] * sc; if (i === a) c.moveTo(x, y); else c.lineTo(x, y); }
          c.stroke();
        } else {
          for (let x = 0; x < W; x++) {
            const i0 = a + Math.floor((x / W) * len), i1 = a + Math.floor(((x + 1) / W) * len);
            let lo = Infinity, hi = -Infinity;
            for (let i = i0; i < Math.max(i0 + 1, i1); i++) { lo = Math.min(lo, data[i]); hi = Math.max(hi, data[i]); }
            c.fillRect(x, mid - hi * sc, 1, Math.max(1, (hi - lo) * sc));
          }
        }
        c.setLineDash([]);
        c.fillStyle = T.line;
        c.fillRect(0, mid, W, 1);
      };
      // Warm-up samples are stored as they are.
      if (v.warmup > 0 && v.warmup > a) {
        c.fillStyle = alpha(T.kind.header, 0.14);
        c.fillRect(0, 0, xOf(Math.min(b, v.warmup)) - 0.5 * (W / len), TOP);
      }
      lane(0, TOP, v.signal, T.kind.audio);
      if (len <= W) lane(0, TOP, v.predicted, T.kind.header, true);
      // Rice partitions under the residual.
      const y1 = TOP + GAP;
      v.partitions?.forEach((p, i) => {
        if (p.end <= a || p.start >= b) return;
        const x0 = Math.max(0, xOf(p.start) - 0.5 * (W / len)), x1 = Math.min(W, xOf(p.end) - 0.5 * (W / len));
        if (i % 2) { c.fillStyle = alpha(T.text, 0.05); c.fillRect(x0, y1, x1 - x0, BOT); }
        c.fillStyle = T.muted;
        c.font = '10px "JetBrains Mono", monospace';
        const lab = p.escaped ? 'raw' : `k=${p.rice}`;
        if (x1 - x0 > c.measureText(lab).width + 6) c.fillText(lab, x0 + 3, y1 + BOT + 11);
      });
      lane(y1, BOT, v.residual, T.kind.index);
      c.fillStyle = T.muted;
      c.font = '10px Inter, sans-serif';
      c.fillText('signal', 4, 11);
      if (len <= W) { c.fillStyle = T.kind.header; c.fillText('prediction (dashed)', 44, 11); }
      c.fillStyle = T.muted;
      c.fillText(`residual (same scale · max ±${resPeak.toLocaleString('en-US')})`, 4, y1 + 10);
    };
    redraw();
  });
  const zoomBtns = h('div', { class: 'seg cv-zoom' });
  const opts: [string, [number, number]][] = [['Whole block', [0, N]], ['First 64 samples', [0, Math.min(N, 64)]], ['Samples 1,000–1,064', [Math.min(1000, Math.max(0, N - 64)), Math.min(N, 1064)]]];
  opts.forEach(([label, sp], k) => {
    if (k === 2 && N < 1100) return;
    const b = h('button', { class: 'seg-btn', 'aria-pressed': String(k === 0) }, label);
    b.onclick = () => {
      span = sp;
      zoomBtns.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      redraw();
    };
    zoomBtns.append(b);
  });
  cv.addEventListener('pointermove', (e) => {
    const r = cv.getBoundingClientRect();
    const i = span[0] + Math.max(0, Math.min(span[1] - span[0] - 1, Math.floor(((e.clientX - r.left) / r.width) * (span[1] - span[0]))));
    showTip(`<b>Sample ${i.toLocaleString('en-US')}</b>${i < v.warmup ? ' (warm-up, stored as is)' : ''}<br><span class="mono">actual ${v.signal[i]}<br>predicted ${v.predicted[i]}<br>residual ${v.residual[i]}</span>`, e.clientX, e.clientY);
  });
  cv.addEventListener('pointerleave', hideTip);
  const meta = `${N.toLocaleString('en-US')} samples · ${(v.bits / N).toFixed(2)} bits/sample (raw ${v.bitsPerSample})`;
  return h('figure', { class: 'cv-fig' },
    h('figcaption', {}, h('b', {}, v.title), h('span', { class: 'cv-meta' }, meta)),
    zoomBtns, cv,
    h('p', { class: 'cv-cap', html: richText(v.caption) }));
}

// ------------------------------------------------------------------ bars
function barsChart(v: BarsView): HTMLElement {
  const max = Math.max(1e-9, ...v.items.map((i) => i.value));
  const T = theme();
  const rows = v.items.map((it, k) =>
    h('div', { class: 'cv-bar', title: it.note ?? '' },
      h('span', { class: 'cv-bar-l' }, it.label),
      h('span', { class: 'cv-bar-t' }, h('span', { style: { width: `${(it.value / max) * 100}%`, background: [T.kind.audio, T.kind.index, T.kind.metadata, T.kind.header][k % 4] } })),
      h('span', { class: 'cv-bar-v' }, `${it.value.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${v.unit}`)));
  return h('figure', { class: 'cv-fig' }, h('figcaption', {}, h('b', {}, v.title)), ...rows, h('p', { class: 'cv-cap', html: richText(v.caption) }));
}
